/**
 * ./exercise3/Exercise3Workout.js
 *
 * This module **guides users through Exercise 3**, which focuses on **peripheral vision training** 
 * while maintaining **rhythmic breathing**. The exercise includes:
 * - **Breathing animation synchronized with inhale, hold, and exhale phases.**
 * - **Randomized word pairs appearing on opposite sides of the screen.**
 * - **Dynamic timing controls & progress tracking.**
 * - **Session completion with transition to summary screen.**
 *
 */


import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './Exercise3Workout.css';
import Circle1 from '../components/icons/circlein1.svg';
import Circle2 from '../components/icons/circlein2.svg';
import Circle3 from '../components/icons/circlein3.svg';
import Circle4 from '../components/icons/circlein4.svg';
import Circle5 from '../components/icons/circlein5.svg';
import Circle6 from '../components/icons/circlein6.svg';
import Circle7 from '../components/icons/circlein7.svg';

import stick1 from "../components/icons/stick1.svg";
import stick2 from "../components/icons/stick2.svg";
import stick3 from "../components/icons/stick3.svg";
import stick4 from "../components/icons/stick4.svg";
import stick5 from "../components/icons/stick5.svg";
import stick6 from "../components/icons/stick6.svg";
import stick7 from "../components/icons/stick7.svg";

import CloseIcon from '../components/icons/close.svg';
import InfoIcon from '../components/icons/InfoIcon.svg'; 
import PlayIcon from '../components/icons/play.svg';
import StopIcon from '../components/icons/stop.svg';
import Curtain from '../components/Curtain';
import OpenAI from 'openai';
import person1 from '../components/icons/person1.svg';
import person2 from '../components/icons/person2.svg';
import person3 from '../components/icons/person3.svg';
import person4 from '../components/icons/person4.svg';
import person5 from '../components/icons/person5.svg';
import person6 from '../components/icons/person6.svg';
import person7 from '../components/icons/person7.svg';
import { FALLBACK_WORDS } from '../components/fallbackWords';
import { updateUserProgress } from '../utility';
import { DEBUG_TIME } from "../config";

function Exercise3Workout() {
  const navigate = useNavigate();
  const location = useLocation(); // Add this line to get location state
  const [long, setLong] = useState(3.0);
  const [short, setShort] = useState(2.0);
  const [hold, setHold] = useState(1.0);
  const [hasStartedOnce, setHasStartedOnce] = useState(false);
  const [wordChangeInterval, setWordChangeInterval] = useState(1000); // speed of word change
  const duration = location.state?.duration || 3; // Default to 3 minutes if not specified
  const [isPressed, setIsPressed] = useState(false);
  const [errorRate, setErrorRate] = useState(0);

  // Update initial time state to use passed duration
  const [time, setTime] = useState(() => {
    return DEBUG_TIME ?? location.state?.time ?? duration * 60; // Convert minutes to seconds
  });
  
  // Update progress calculation
  const totalTime = location.state?.duration * 60 || 180; // Store total time in seconds
  
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(1);
  const [nextStep, setNextStep] = useState(2);
  const [previousStep, setPreviousStep] = useState(null);
  const [fadeInStyle, setFadeInStyle] = useState({ opacity: 1 });
  const [fadeOutStyle, setFadeOutStyle] = useState({ opacity: 0 });
  const [isTransitioningToOne, setIsTransitioningToOne] = useState(false);
  const [showCurtain, setShowCurtain] = useState(false);
  const [wordPairs, setWordPairs] = useState([]);
  const [currentWordPair, setCurrentWordPair] = useState(['Play', 'Play']);
  const [wordPairIndex, setWordPairIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMoreWords, setIsLoadingMoreWords] = useState(false);
  const [openai, setOpenai] = useState(null);

  // Add new states from Exercise1
  const timeRef = useRef(0);
  const [instructionText, setInstructionText] = useState('Play');
  const [showPlayIcon, setShowPlayIcon] = useState(true);
  const [showStopIcon, setShowStopIcon] = useState(false);

  useEffect(() => {
    // Load values from localStorage
    setLong(parseFloat(localStorage.getItem('long')) || 2.0);
    setShort(parseFloat(localStorage.getItem('short')) || 1.0);
    setHold(parseFloat(localStorage.getItem('hold')) || 1.0);
  }, []);

  // Initialize OpenAI client when component mounts
  useEffect(() => {
    const client = new OpenAI({
      apiKey: process.env.REACT_APP_OPENAI_API_KEY,
      dangerouslyAllowBrowser: true
    });
    setOpenai(client);
  }, []);

  const fetchWordPairs = async (numPairs = 50) => {
    try {
      if (!openai) {
        throw new Error('OpenAI client not initialized');
      }

      const response = await openai.chat.completions.create({
        model: "gpt-3.5-turbo",
        messages: [
          { role: "system", content: "Generate simple, single commonly used words in pairs." },
          { role: "user", content: `Generate ${numPairs * 2} simple, single commonly used English words. Separate them with commas. These will be shown in pairs. Avoid technical terms, jargon, or multi-word phrases.` }
        ],
        max_tokens: 100
      });

      const words = response.choices[0].message.content.split(',').map(word => word.trim());
      // Convert array of words into pairs
      const pairs = [];
      for (let i = 0; i < words.length; i += 2) {
        pairs.push([words[i], words[i + 1] || '']);
      }
      return pairs;
    } catch (error) {
      console.error('Error generating word pairs:', error);
      // Generate random pairs from fallback words
      const pairs = [];
      for (let i = 0; i < numPairs; i++) {
        const word1 = FALLBACK_WORDS[Math.floor(Math.random() * FALLBACK_WORDS.length)];
        const word2 = FALLBACK_WORDS[Math.floor(Math.random() * FALLBACK_WORDS.length)];
        pairs.push([word1, word2]);
      }
      return pairs;
    }
  };

  useEffect(() => {
    // Fetch initial word pairs when the component mounts
    async function loadInitialWordPairs() {
      setIsLoading(true);
      const initialPairs = await fetchWordPairs(50);
      setWordPairs(initialPairs);
      setIsLoading(false);
    }
    loadInitialWordPairs();
  }, []);

  useEffect(() => {
    let wordInterval;
    if (isPlaying && wordPairs.length > 0 && !isLoading) {
      setCurrentWordPair(wordPairs[wordPairIndex]);
      wordInterval = setInterval(() => {
        setWordPairIndex((prevIndex) => {
          const newIndex = (prevIndex + 1) % wordPairs.length;
          setCurrentWordPair(wordPairs[newIndex]);
          
          // Check if we need to fetch more word pairs
          if (newIndex >= wordPairs.length - 3 && !isLoadingMoreWords) {
            fetchMoreWordPairs();
          }
          
          return newIndex;
        });
      }, wordChangeInterval);
    } else if (!isPlaying && hasStartedOnce && !isLoading) {
      setCurrentWordPair(wordPairs[wordPairIndex] || ['Play', 'Play']);
    } else if (!hasStartedOnce || isLoading) {
      setCurrentWordPair(['Play', 'Play']);
    }
    return () => clearInterval(wordInterval);
  }, [isPlaying, wordPairs, isLoading, wordPairIndex, hasStartedOnce, isLoadingMoreWords, wordChangeInterval]);

  const fetchMoreWordPairs = async () => {
    setIsLoadingMoreWords(true);
    const newPairs = await fetchWordPairs(50);
    setWordPairs(prevPairs => [...prevPairs, ...newPairs]);
    setIsLoadingMoreWords(false);
  };

  useEffect(() => {
    let interval;
    if (isPlaying && time > 0) {
      interval = setInterval(() => {
        setTime(prevTime => {
          const newTime = prevTime - 0.1;
          setProgress(100 - (newTime / totalTime) * 100); // Use totalTime instead of hardcoded 180
          return newTime > 0 ? newTime : 0;
        });
      }, 100);
    } else if (time === 0) {
      setIsPlaying(false);
    }
    return () => clearInterval(interval);
  }, [isPlaying, time, totalTime]);

  // Update navigation effect
  useEffect(() => {
    if (time <= 0) {
    

      setIsPlaying(false);
      setShowCurtain(true);
      setTimeout(() => {
        navigate("/exercise3finish", {
          state: {
            timeSpent: timeRef.current,
            duration: duration,
            errorRate: errorRate
          },
        });
      }, 2000);
    }
  }, [time, navigate]);

  // Add time tracking effect
  useEffect(() => {
    let timer;
    if (isPlaying) {
      timer = setInterval(() => {
        timeRef.current += 1;
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [isPlaying]);

  // Update animation effect
  useEffect(() => {
    let timeout;
    if (isPlaying) {
      const steps = [
        [stick1, stick1], // Pair of sticks for Step 1
        [stick2, stick2], // Pair of sticks for Step 2
        [stick3, stick3],
        [stick4, stick4],
        [stick5, stick5],
        [stick6, stick6],
        [stick7, stick7],
      ];
      const durations = [long, long, long, short, short, short, short];
      
      const fadeInNextCircle = () => {
        timeout = setTimeout(() => {
          setCurrentStep(nextStep);
          
          if (currentStep == 2 || currentStep == 3) {
            setInstructionText('Inhale');
          } else if (currentStep == 4) {
            setInstructionText('Hold');
          } else if (currentStep >= 5 && currentStep <= 7) {
            setInstructionText('Exhale');
          } else {
            setInstructionText('Hold');
          }

          if (nextStep === 1) {
            setIsTransitioningToOne(true);
            setFadeInStyle({ opacity: 1, transition: `opacity 0.2s ease-in` });
            setFadeOutStyle({ opacity: 0, transition: `opacity 0.2s ease-out` });
          } else {
            setIsTransitioningToOne(false);
            setFadeInStyle({ opacity: 1, transition: `opacity ${durations[nextStep - 1]}s ease-in` });
          }

          timeout = setTimeout(() => {
            setPreviousStep(null);
            timeout = setTimeout(() => {
              setNextStep((prev) => (prev === 7 ? 1 : prev + 1));
              setPreviousStep(currentStep);
              fadeInNextCircle();
            }, hold * 1000);
          }, nextStep === 1 ? 200 : durations[nextStep - 1] * 1000);
        }, 10);
      };

      fadeInNextCircle();
    }

    return () => clearTimeout(timeout);
  }, [isPlaying, nextStep, long, short, hold, currentStep]);

  // Update control functions
  const startBreathing = () => {
    if (!isLoading && wordPairs.length > 0) {
      setIsPlaying(true);
      setNextStep(2);
      setFadeInStyle({ opacity: 1 });
      setShowPlayIcon(false);
      setShowStopIcon(false);
      if (!hasStartedOnce) {
        setHasStartedOnce(true);
        setWordPairIndex(0);
      }
      setCurrentWordPair(wordPairs[wordPairIndex]);
    }
  };

  const stopBreathing = () => {
    setIsPlaying(false);
    setShowStopIcon(true);
    setInstructionText('Pause');
    setCurrentStep(1);
    setNextStep(2);
    setPreviousStep(null);
    setFadeOutStyle({ opacity: 0 });
    setFadeInStyle({ opacity: 1 });
  };

  const resumeBreathing = () => {
    setIsPlaying(true);
    setShowStopIcon(false);
  };

  const handleEdit = () => {
    navigate('/RhythmChoosing', { state: { 
      returnTo: '/exercise3workout',
      duration: duration,
      time: time
     } });
  };

  const handleClose = () => {
    navigate('/');
  };
  // Spacebar feature
    useEffect(() => {
        const handleKeyDown = (event) => {
          if (event.code === "Space") {
            setIsPressed(true);
            setErrorRate(prevError => prevError + 1);


          }
        };

        const handleKeyUp = (event) => {
          if (event.code === "Space") {
            setIsPressed(false);
          }
        };

        window.addEventListener("keydown", handleKeyDown);
        window.addEventListener("keyup", handleKeyUp);

        return () => {
          window.removeEventListener("keydown", handleKeyDown);
          window.removeEventListener("keyup", handleKeyUp);
        };
      }, []);

  return (
    <div className="exercise-container">
      {showCurtain && <Curtain appear={true} duration={2000} />}
      <div className="header-bar">
        <div className="close-icon-wrapper">
          <img src={CloseIcon} alt="close icon" className="close-icon" onClick={handleClose} />
        </div>
        <div className="progress-bar-container">
          <div className="progress-bar">
            <div className="progress" style={{ width: `${progress}%` }}></div>
          </div>
          <span>{`${Math.floor(time / 60)}:${String(Math.floor(time % 60)).padStart(2, '0')}`}</span>
        </div>
        <div className="button-container">
          <button className="save-button" onClick={handleEdit}>Edit Rhythm</button>
        </div>
      </div>

      <div className="exercise3-main-content">
        <div className="circle-container">
          <div className="circle-wrapper">
          {[
              [stick1, stick1],
              [stick2, stick2],
              [stick3, stick3],
              [stick4, stick4],
              [stick5, stick5],
              [stick6, stick6],
              [stick7, stick7],
            ].map((StickPair, index) => (
              <div key={index} className="stick-container">
                {/* Left Stick */}
              <img
                src={StickPair[0]}
                alt={`stick left step ${index + 1}`}
                className="breathing-stick left-stick"
                style={
                  currentStep === index + 1
                    ? fadeInStyle
                    : previousStep === index + 1 ||
                      (index === 0 && currentStep === 2)
                    ? index === 6 && isTransitioningToOne
                      ? fadeOutStyle
                      : { opacity: 1 }
                    : { opacity: 0 }
                }
              />
                {/* Right Stick */}
              <img
                src={StickPair[1]}
                alt={`stick right step ${index + 1}`}
                className="breathing-stick right-stick"
                style={
                  currentStep === index + 1
                    ? fadeInStyle
                    : previousStep === index + 1 ||
                      (index === 0 && currentStep === 2)
                    ? index === 6 && isTransitioningToOne
                      ? fadeOutStyle
                      : { opacity: 1 }
                    : { opacity: 0 }
                }
              />
              </div>
            ))}
            {[person1, person2, person3, person4, person5, person6, person7].map((PersonImg, index) => (
              <img
                key={`person-${index}`}
                src={PersonImg}
                alt={`person step ${index + 1}`}
                className="breathing-circle"
                style={{
                  position: 'absolute',
                  height: '55%',
                  width: 'auto',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  ...(currentStep === index + 1
                    ? fadeInStyle
                    : previousStep === index + 1 || (index === 0 && currentStep === 2)
                    ? index === 6 && isTransitioningToOne ? fadeOutStyle : { opacity: 1 }
                    : { opacity: 0 }),
                  cursor: 'pointer',
                  pointerEvents: currentStep === index + 1 ? 'auto' : 'none'
                }}
                onClick={() => {
                  if (currentStep === index + 1) {
                    stopBreathing();
                  }
                }}
              />
            ))}
            {showPlayIcon && (
              <img
                src={PlayIcon}
                alt="play icon"
                className="play-icon"
                onClick={startBreathing}
              />
            )}
            {showStopIcon && (
              <img
                src={StopIcon}
                alt="stop icon"
                className="play-icon"
                style={{ opacity: 1 }}
                onClick={resumeBreathing}
              />
            )}
          </div>
        </div>

        <div className="exercise3-word-display">
          <span className="left-word3">{currentWordPair[0]}</span>
          <span className="right-word3">{currentWordPair[1]}</span>
        </div>

        <div className="breathing-controls">
          <div className="control">
            <div className='row2'>
              <span>{instructionText}</span>
            </div>
          </div>
        </div>
      </div>
      <div className={`spacebar-btn ${isPressed ? "pressed" : ""}`}>
          SPACE
        </div>

      <div className="instruction-box">
        <img src={InfoIcon} alt="info icon" className="instruction-icon" />
        <ul>
            <li>Rhythmically breathe while inhaling, holding, exhaling, and holding again. Repeat this cycle.</li>
            <li>Press the spacebar whenever you feel that you've made a mistake in word comprehension.</li>
          </ul>
      </div>
    </div>
  );
}

export default Exercise3Workout;
